import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { DbSource } from '@makyoapp/makyo-common'
import { Airtable, Excel, Makyo } from '@makyoapp/makyo-icon/colored'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { SpreadsheetFilled24 } from '@makyoapp/makyo-icon/icon'

interface DbSourceCardSmallProps {
  className?: string
  active?: boolean
  title: React.ReactNode
  icon: React.ReactNode
  onClick?: React.MouseEventHandler
  isAI?: boolean
}

export function DbSourceCardSmall(props: DbSourceCardSmallProps) {
  const isStrIcon = typeof props.icon !== 'object'

  return (
    <div
      role='button'
      className={clsx(
        'flex-c rounded-sm',
        props.isAI
          ? 'p-[2px] bg-linear-to-r from-primary-500 via-purple-500 to-pink-500 hover:bg-linear-to-br'
          : 'px-4 py-2 bdr border-2',
        props.active ? 'border-primary-500' : '',
        props.className
      )}
      onClick={props.onClick}
    >
      <div className={clsx('flex-c w-full', props.isAI && 'bg-white dark:bg-slate-800 rounded-xs px-4 py-2')}>
        {isStrIcon ? <img src={props.icon?.toString()} width={16} height={16} /> : props.icon}
        <div className='font-semibold'>{props.title}</div>
      </div>
    </div>
  )
}

interface DbSourceCardProps {
  className?: string
  active?: boolean
  title: React.ReactNode
  description: React.ReactNode
  icon: React.ReactNode
  info?: string
  onClick?: React.MouseEventHandler
}

function DbSourceCard(props: DbSourceCardProps) {
  return (
    <button
      className={clsx(
        'flex flex-col items-center p-8 h-[170px]',
        'border',
        'hover:bg-zinc-100 dark:hover:bg-slate-700',
        'relative cursor-pointer',
        props.active ? 'border-primary-500' : 'bdr',
        props.className
      )}
      onClick={props.onClick}
    >
      {props.icon}
      <div className='pt-4 font-semibold'>{props.title}</div>
      <div className='text-light'>{props.description}</div>
      <div className='absolute top-2.5 right-2.5' title={props.info}>
        <Icon.Info16 />
      </div>
    </button>
  )
}

interface DbSourceCardBtnProps {
  onClick: (source: DbSource, e: React.MouseEvent) => void
  className?: string
  active?: boolean
}

function MakyoCard(props: DbSourceCardBtnProps) {
  const { t } = useTranslation()

  return (
    <DbSourceCard
      icon={<Makyo className='fill-primary-500' width={48} height={48} />}
      title={'Makyo DB'}
      description={t('Built-in Makyo database feature')}
      info={t('long tooltip info goes in here')}
      onClick={(e) => props.onClick?.(DbSource.Makyo, e)}
      active={props.active}
      className={props.className}
    />
  )
}

function AirtableCard(props: DbSourceCardBtnProps) {
  const { t } = useTranslation()

  return (
    <DbSourceCard
      icon={<Airtable width={48} height={48} />}
      title={'Airtable'}
      description={t('Link data from {{source}}', {
        source: 'airtable',
      })}
      info={t('long tooltip info goes in here')}
      onClick={(e) => props.onClick?.(DbSource.AirTable, e)}
      active={props.active}
      className={props.className}
    />
  )
}

function SpreadsheetCard(props: DbSourceCardBtnProps) {
  const { t } = useTranslation()

  return (
    <DbSourceCard
      icon={<SpreadsheetFilled24 width={48} height={48} className='fill-gss' />}
      title='Google Spreadsheet'
      description={t('Link data from {{source}}', {
        source: 'google spreadsheet',
      })}
      info={t('long tooltip info goes in here')}
      onClick={(e) => props.onClick?.(DbSource.SpreadSheet, e)}
      active={props.active}
      className={props.className}
    />
  )
}

function ExcelCard(props: DbSourceCardBtnProps) {
  const { t } = useTranslation()

  return (
    <DbSourceCard
      icon={<Excel width={48} height={48} />}
      title='Microsoft Excel'
      description={t('Link data from {{source}}', {
        source: 'microsoft excel',
      })}
      info={t('long tooltip info goes in here')}
      onClick={(e) => props.onClick?.(DbSource.Excel, e)}
      active={props.active}
      className={props.className}
    />
  )
}

DbSourceCard.Makyo = MakyoCard
DbSourceCard.Airtable = AirtableCard
DbSourceCard.Spreadsheet = SpreadsheetCard
DbSourceCard.Excel = ExcelCard
